export interface IRoute {
  path: string;
  name?: string;
  component: string;
  redirect?: string;
  layout?: string | boolean;
  hideInMenu?: boolean;
  menuRender?: boolean;
  headerRender?: boolean;
  routes?: IRoute[];
}

/**
 * @name umi 的路由配置
 * @description 只支持 path,component,routes,redirect,wrappers,name,icon 的配置
 * @param path  path 只支持两种占位符配置，第一种是动态参数 :id 的形式，第二种是 * 通配符，通配符只能出现路由字符串的最后。
 * @param component 配置 location 和 path 匹配后用于渲染的 React 组件路径。可以是绝对路径，也可以是相对路径，如果是相对路径，会从 src/pages 开始找起。
 * @param routes 配置子路由，通常在需要为多个路径增加 layout 组件时使用。
 * @param redirect 配置路由跳转
 * @param wrappers 配置路由组件的包装组件，通过包装组件可以为当前的路由组件组合进更多的功能。 比如，可以用于路由级别的权限校验
 * @param name 配置路由的标题，默认读取国际化文件 menu.ts 中 menu.xxxx 的值，如配置 name 为 login，则读取 menu.ts 中 menu.login 的取值作为标题
 * @param icon 配置路由的图标，取值参考 https://ant.design/components/icon-cn， 注意去除风格后缀和大小写，如想要配置图标为 <StepBackwardOutlined /> 则取值应为 stepBackward 或 StepBackward，如想要配置图标为 <UserOutlined /> 则取值应为 user 或者 User
 * @doc https://umijs.org/docs/guides/routes
 */
export default [
  {
    path: '/user',
    layout: false,
    routes: [
      {
        name: 'login',
        path: '/user/login',
        component: './Login',
      },
    ],
  },
  {
    path: '/home',
    name: '首页',
    // icon: 'smile',
    component: './Home',
  },
  {
    path: '/aidialogue',
    name: '全库问答',
    component: './AIDialogue',
  },
  {
    path: '/search',
    name: '智能检索',
    // icon: 'search',
    component: './Search',
  },
  {
    path: '/document',
    name: '全部文档',
    hideInMenu: true,
    routes: [
      {
        path: '/document',
        redirect: '/document/list',
      },
      {
        name: '全部文档',
        path: '/document/list',
        component: './Document/list',
      },
      {
        name: '全库问答',
        path: '/document/chat',
        component: './Document/Chat',
      },
    ],
  },
  {
    path: '/manage',
    name: '文控中心',
    // icon: 'user',
    routes: [
      {
        path: '/manage',
        redirect: '/manage/user-center',
      },
      {
        path: '/manage/user-center',
        name: '我的主页',
        component: './User/index',
      },
      // 工作台
      {
        path: '/manage/workbench',
        name: '工作台',
        routes: [
          {
            path: '/manage/workbench',
            redirect: '/manage/workbench/todo',
          },
          // 待办
          {
            path: '/manage/workbench/todo',
            name: '我的待办',
            component: './Todo/WaitList',
          },
          // 已办
          {
            path: '/manage/workbench/done',
            name: '我的已办',
            component: './Todo/Finish',
          },
          // 已发
          {
            path: '/manage/workbench/sent',
            name: '已发事项',
            component: './Todo/Create',
          },
          // 处理事件详情
          {
            path: '/manage/workbench/flow/:flowId/:nodeId/:affairId',
            name: '处理事件详情',
            hideInMenu: true,
            menuRender: false,
            component: './Todo/Detail',
          },
        ],
      },
      // 文档管理 - 我的收藏/回收站
      {
        path: '/manage/document',
        name: '文档中心',
        routes: [
          {
            path: '/manage/document',
            redirect: '/manage/document/file',
          },
          // 我的文档
          {
            path: '/manage/document/upload',
            name: '我的文档',
            component: './User/Document',
          },
          // 我的经验
          {
            path: '/manage/document/experience',
            name: '我的经验',
            component: './User/Experience',
          },
          // 我的任务
          {
            path: '/manage/document/task',
            name: '我的任务',
            component: './User/Task',
            hideInMenu: true,
          },
          // 我的收藏
          {
            path: '/manage/document/file/collect',
            name: '我的收藏',
            component: './User/Collect',
          },
          // 上传文件
          {
            path: '/manage/document/upload/:id',
            name: '上传文件',
            component: './Document/Create/Upload',
            hideInMenu: true,
            menuRender: false,
          },
          // 创建经验
          {
            path: '/manage/document/experience/:id',
            name: '创建经验',
            component: './Document/Create/Experience',
            hideInMenu: true,
            menuRender: false,
            headerRender: false,
            layout: false,
          },
          {
            path: '/manage/document/experience/:id/:action',
            name: '经验详情',
            component: './Document/Create/Experience',
            hideInMenu: true,
            menuRender: false,
            headerRender: false,
            layout: false,
          },
          // 参与写作
          {
            path: '/manage/document/collaboration/:id',
            name: '参与写作',
            component: './Document/Create/Collaboration',
            hideInMenu: true,
            menuRender: false,
            layout: false,
            headerRender: false,
          },
          {
            path: '/manage/document/collaboration/:id/:action',
            name: '写作详情',
            component: './Document/Create/Collaboration',
            hideInMenu: true,
            menuRender: false,
            layout: false,
            headerRender: false,
          },
          // 创建协作
          {
            path: '/manage/document/task/:id',
            name: '创建协作',
            component: './Document/Create/Task',
            hideInMenu: true,
            menuRender: false,
          },
          // 协作详情
          {
            path: '/manage/document/task/:id/detail',
            name: '协作详情',
            component: './User/Task/detail',
            hideInMenu: true,
            menuRender: false,
            // layout: false,
            // headerRender: false,
          },
        ],
      },
      // 文件管理
      {
        path: '/manage/file',
        name: '文件管理',
        routes: [
          {
            path: '/manage/file',
            redirect: '/manage/file/recycle-bin',
          },
          // 我的收藏
          {
            path: '/manage/file/recycle-bin',
            name: '回收站',
            component: './User/RecycleBin',
          },
        ],
      },
      // 详情
      {
        path: '/manage/document/detail/:id',
        name: '文档详情',
        component: './Document/Detail',
        hideInMenu: true,
        // layout: false,
        // headerRender: false,
        menuRender: false,
      },
      // 事项详情
      {
        path: '/manage/document/todo/detail/:id',
        name: '事项详情',
        component: './Todo/Detail',
        hideInMenu: true,
      },
    ],
  },
  {
    path: '/course',
    name: '课程中心',
    // icon: 'book',
    hideInMenu: true,
    menuRender: false,
    routes: [
      {
        path: '/course',
        redirect: '/course/all',
      },
      {
        path: '/course/all',
        name: '全部课程',
        component: './Course/AllCourse',
      },
      {
        path: '/course/my',
        name: '我的课程',
        component: './Course/MyCourse',
      },
      {
        path: '/course/detail/:id',
        name: '课程详情',
        component: './Course/Detail',
        hideInMenu: true,
        // 隐藏二级菜单
        menuRender: false,
      },
      {
        path: '/course/create',
        name: '创建课程',
        component: './Course/Create',
        hideInMenu: true,
        menuRender: false,
      },
    ],
  },
  {
    path: '/statistic',
    name: '统计分析',
    // icon: 'barChart',
    routes: [
      {
        path: '/statistic',
        redirect: '/statistic/index',
      },
      {
        path: '/statistic/index',
        component: './Statistic',
      },
    ],
  },
  {
    path: '/profile',
    name: '个人中心',
    icon: 'user',
    // layout: false,
    component: './Profile',
    hideInMenu: true,
  },
  {
    path: '/admin',
    name: '后台管理',
    component: './Admin',
    // layout: false,
    layout: 'side',
    // menuRender: false,
    headerRender: false,
    // menuHeaderRender: false,
    hideInMenu: true,
    routes: [
      {
        path: '/admin',
        redirect: '/admin/org',
      },
      {
        path: '/admin/org',
        name: '组织管理',
        hideInMenu: false,
        routes: [
          {
            path: '/admin/org',
            redirect: '/admin/org/info',
          },
          {
            path: '/admin/org/info',
            name: '组织信息',
            component: './Admin/Organization/Info',
          },
          {
            path: '/admin/org/user',
            name: '用户管理',
            component: './Admin/Organization/User',
          },
          {
            path: '/admin/org/role',
            name: '角色管理',
            component: './Admin/Organization/Role',
          },
          {
            path: '/admin/org/dept',
            name: '部门管理',
            component: './Admin/Organization/Dept',
          },
        ],
      },
      {
        path: '/admin/system',
        name: '系统管理',
        hideInMenu: false,
        routes: [
          {
            path: '/admin/system',
            redirect: '/admin/system/parameter',
          },
          {
            path: '/admin/system/parameter',
            name: '参数管理',
            component: './Admin/System/Parameter',
          },
          {
            path: '/admin/system/openapi',
            name: '凭证管理',
            component: './Admin/System/OpenApi',
          },
        ],
      },
      // 知识库管理
      {
        path: '/admin/knowledge',
        name: '知识库管理',
        hideInMenu: false,
        routes: [
          {
            path: '/admin/knowledge',
            redirect: '/admin/knowledge/tag',
          },
          {
            path: '/admin/knowledge/document',
            name: '文档管理',
            component: './Admin/KnowledgeBase/Document',
          },
          {
            path: '/admin/knowledge/tag',
            name: '标签管理',
            component: './Admin/KnowledgeBase/Tag',
          },
          {
            path: '/admin/knowledge/subject',
            name: '专题管理',
            component: './Admin/KnowledgeBase/Subject',
          },
        ],
      },
      {
        path: '/admin/course',
        name: '课程管理',
        hideInMenu: false,
        routes: [
          {
            path: '/admin/course',
            redirect: '/admin/course/category',
          },
          {
            path: '/admin/course/category',
            name: '课程分类',
            component: './Admin/Course/Category',
          },
        ],
      },
    ],
  },
  {
    path: '/',
    redirect: '/home',
  },
  {
    path: '*',
    layout: false,
    component: './404',
  },
] as IRoute[];
